import { useEffect, useState } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import { HttpMethodProps } from "../../lib/types";
import { HttpCodes } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  data?: any;
  showError?: boolean;
  doNotUseAuthorization?: boolean;
  refreshKey?: number | string;
  skip?: boolean;
}
export function usePerformRequest<Type>({
  method,
  route,
  data: body,
  showError = true,
  doNotUseAuthorization = false,
  refreshKey,
  skip,
}: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  const reloadData = async () => {
    if (skip || !route) return;
    const token = localStorage.getItem("t");
    const headers: any = {};
    if (!doNotUseAuthorization && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const config: AxiosRequestConfig = {
      headers,
      method,
      data: body,
      url: route,
      timeout: 30000, // fail fast instead of hanging on a stalled mobile connection
    };
    setLoading(true);
    setError(undefined); // clear any stale error from a previous (failed) load

    // Auto-retry transient failures (dropped/timed-out request or a 5xx) so a
    // flaky mobile connection self-heals instead of showing "couldn't load".
    // Only idempotent GETs are retried; auth failures bail immediately.
    const verb = String(method || "GET").toUpperCase();
    const maxAttempts = verb === "GET" ? 3 : 1;
    let lastError: AxiosError<Type, Type> | undefined;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const r = await axios.request(config);
        setResponse(r as AxiosResponse);
        setStatus(r.status);
        setLoading(false);
        setData((r as any)?.data ?? undefined);
        return;
      } catch (e) {
        const err = e as AxiosError<Type, Type>;
        const httpStatus = err.response?.status;
        if (httpStatus === HttpCodes.Unauthorized) {
          setError(err);
          setStatus(httpStatus as number);
          setLoading(false);
          setData(undefined);
          localStorage.removeItem("t");
          window.location.href = "/";
          return;
        }
        lastError = err;
        const transient = !httpStatus || httpStatus >= 500; // network/timeout or server error
        if (attempt < maxAttempts && transient) {
          await new Promise((res) => setTimeout(res, 700 * attempt)); // 0.7s, 1.4s backoff
          continue;
        }
        break;
      }
    }
    // Every attempt failed — surface the error so the page can show a retry state.
    setError(lastError);
    setStatus((lastError?.response?.status as number) ?? "undefined");
    setLoading(false);
    setData(undefined);
  };
  useEffect(() => {
    if (!skip && route) {
      reloadData().catch(() => setLoading(false));
    }
  }, [route, refreshKey, skip]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: HttpMethodProps;
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  onError?: () => any;
  config?: Partial<AxiosRequestConfig>;
}

export async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  onError,
  config: extraConfig,
}: PerformRequestOptions) {
  const token = localStorage.getItem("t");

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `Bearer ${token}`, ...headers };
  const config: AxiosRequestConfig = {
    method,
    data,
    url: route,
    headers: requestHeaders,
    ...extraConfig,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (onError) onError();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });

  return {
    status: r.status as number,
    data: r?.data as Type,
    message: r?.message ?? undefined,
  };
}
export async function UploadFiles(
  files: File[],
  uploadRoute: string = Endpoints.UploadFile().route,
) {
  const promises = files.map(async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    // PerformRequest handles Auth
    // Axios handles Content-Type for FormData
    return PerformRequest<any>({
      method: "POST",
      route: uploadRoute,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  });

  return Promise.all(promises);
}
